/**
 * Notifications data hooks — queries + optimistically-updated mutations.
 *
 * Optimistic-update shape mirrors `useProductDev.ts` (onMutate patches the
 * cache, onError rolls back a snapshot, onSettled invalidates for server
 * truth), scaled down. The list is displayed in two places at once — the bell
 * popover (a plain `useQuery`) and the inbox (an `useInfiniteQuery`) — so every
 * cache write fans out across BOTH shapes via a prefix-matched
 * `setQueriesData`, and the unread-count cache is adjusted in lockstep so the
 * bell badge drops the instant the operator acts, from either surface.
 *
 * All procedure access is routed through the `notificationsApi` seam cast so
 * this file stays fully typed while the backend `notifications` router lands in
 * parallel (see `notifications-procedures.ts`).
 */
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  notificationsApi,
  NOTIFICATIONS_LIST_KEY,
  NOTIFICATIONS_UNREAD_KEY,
  type NotificationFilter,
  type NotificationItem,
  type NotificationListPage,
} from './notifications-procedures';

const POPOVER_LIMIT = 8;
const INBOX_PAGE_SIZE = 20;

// ---- Cache-shape helpers ---------------------------------------------------
// A cached list value is either a single `NotificationListPage` (the plain
// popover query) or `{ pages: NotificationListPage[] }` (the infinite inbox
// query). These helpers read/patch both without the caller caring which.

function isInfinite(
  data: unknown,
): data is { pages: NotificationListPage[]; pageParams: unknown[] } {
  return typeof data === 'object' && data !== null && 'pages' in data;
}

function collectCachedItems(data: unknown): NotificationItem[] {
  if (!data) return [];
  if (isInfinite(data)) return data.pages.flatMap((p) => p.items ?? []);
  const page = data as NotificationListPage;
  return Array.isArray(page.items) ? page.items : [];
}

function mapCachedList(
  data: unknown,
  fn: (items: NotificationItem[]) => NotificationItem[],
): unknown {
  if (!data) return data;
  if (isInfinite(data)) {
    return {
      ...data,
      pages: data.pages.map((p) => ({ ...p, items: fn(p.items ?? []) })),
    };
  }
  const page = data as NotificationListPage;
  if (Array.isArray(page.items)) return { ...page, items: fn(page.items) };
  return data;
}

function adjustUnread(qc: QueryClient, delta: number) {
  if (delta === 0) return;
  qc.setQueriesData<number>({ queryKey: NOTIFICATIONS_UNREAD_KEY }, (old) =>
    typeof old === 'number' ? Math.max(0, old + delta) : old,
  );
}

interface Snapshot {
  prevLists: [readonly unknown[], unknown][];
  prevUnread: [readonly unknown[], unknown][];
}

function snapshot(qc: QueryClient): Snapshot {
  return {
    prevLists: qc.getQueriesData({ queryKey: NOTIFICATIONS_LIST_KEY }),
    prevUnread: qc.getQueriesData({ queryKey: NOTIFICATIONS_UNREAD_KEY }),
  };
}

function restore(qc: QueryClient, ctx: Snapshot | undefined) {
  if (!ctx) return;
  for (const [key, data] of ctx.prevLists) qc.setQueryData(key, data);
  for (const [key, data] of ctx.prevUnread) qc.setQueryData(key, data);
}

async function cancel(qc: QueryClient) {
  await qc.cancelQueries({ queryKey: NOTIFICATIONS_LIST_KEY });
  await qc.cancelQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY });
}

function invalidate(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_KEY });
  void qc.invalidateQueries({ queryKey: NOTIFICATIONS_UNREAD_KEY });
}

/** Count how many of `ids` are currently unread across every cached list. */
function countUnread(qc: QueryClient, ids: Set<string>): number {
  const seen = new Set<string>();
  for (const [, data] of qc.getQueriesData({ queryKey: NOTIFICATIONS_LIST_KEY })) {
    for (const item of collectCachedItems(data)) {
      if (ids.has(item.id) && !item.read_at) seen.add(item.id);
    }
  }
  return seen.size;
}

// ---- Queries ---------------------------------------------------------------

/**
 * Unread count for the bell badge. Polls every 30s but NOT while the tab is
 * backgrounded, and refetches on window focus so the badge is fresh the moment
 * the operator returns.
 */
export function useUnreadCount() {
  return notificationsApi.unreadCount.useQuery(undefined, {
    staleTime: 15_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

/** Recent notifications for the bell popover. Only fetches while open. */
export function useNotificationPreview(open: boolean) {
  return notificationsApi.list.useQuery(
    { filter: 'all', limit: POPOVER_LIMIT },
    { enabled: open, staleTime: 15_000, refetchOnWindowFocus: true },
  );
}

/** Paginated inbox list for the given filter. */
export function useNotificationInbox(filter: NotificationFilter) {
  const query = notificationsApi.list.useInfiniteQuery(
    { filter, limit: INBOX_PAGE_SIZE },
    {
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
    },
  );
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  return { ...query, items };
}

// ---- Mutations -------------------------------------------------------------

export function useNotificationActions() {
  const qc = useQueryClient();
  const nowIso = () => new Date().toISOString();

  const markRead = notificationsApi.markRead.useMutation<Snapshot>({
    onMutate: async ({ ids }) => {
      await cancel(qc);
      const snap = snapshot(qc);
      const idSet = new Set(ids);
      const delta = countUnread(qc, idSet);
      const stamp = nowIso();
      qc.setQueriesData({ queryKey: NOTIFICATIONS_LIST_KEY }, (data) =>
        mapCachedList(data, (items) =>
          items.map((it) =>
            idSet.has(it.id) && !it.read_at ? { ...it, read_at: stamp } : it,
          ),
        ),
      );
      adjustUnread(qc, -delta);
      return snap;
    },
    onError: (_err, _vars, ctx) => restore(qc, ctx),
    onSettled: () => invalidate(qc),
  });

  const markAllRead = notificationsApi.markAllRead.useMutation<Snapshot>({
    onMutate: async () => {
      await cancel(qc);
      const snap = snapshot(qc);
      const stamp = nowIso();
      qc.setQueriesData({ queryKey: NOTIFICATIONS_LIST_KEY }, (data) =>
        mapCachedList(data, (items) =>
          items.map((it) => (it.read_at ? it : { ...it, read_at: stamp })),
        ),
      );
      qc.setQueriesData<number>({ queryKey: NOTIFICATIONS_UNREAD_KEY }, () => 0);
      return snap;
    },
    onError: (_err, _vars, ctx) => restore(qc, ctx),
    onSettled: () => invalidate(qc),
  });

  const dismiss = notificationsApi.dismiss.useMutation<Snapshot>({
    onMutate: async ({ id }) => {
      await cancel(qc);
      const snap = snapshot(qc);
      const delta = countUnread(qc, new Set([id]));
      qc.setQueriesData({ queryKey: NOTIFICATIONS_LIST_KEY }, (data) =>
        mapCachedList(data, (items) => items.filter((it) => it.id !== id)),
      );
      adjustUnread(qc, -delta);
      return snap;
    },
    onError: (_err, _vars, ctx) => restore(qc, ctx),
    onSettled: () => invalidate(qc),
  });

  const dismissAll = notificationsApi.dismissAll.useMutation<Snapshot>({
    onMutate: async () => {
      await cancel(qc);
      const snap = snapshot(qc);
      qc.setQueriesData({ queryKey: NOTIFICATIONS_LIST_KEY }, (data) =>
        mapCachedList(data, () => []),
      );
      qc.setQueriesData<number>({ queryKey: NOTIFICATIONS_UNREAD_KEY }, () => 0);
      return snap;
    },
    onError: (_err, _vars, ctx) => restore(qc, ctx),
    onSettled: () => invalidate(qc),
  });

  return { markRead, markAllRead, dismiss, dismissAll };
}
